import { action, computed, observable, makeAutoObservable } from 'mobx';
import moment from 'moment';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import {
    EventListGroupEventItemType,
    EventsCollectionQueryType,
} from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { SportsbookState } from 'src/domains/sportsbook/trading/state/SportsbookState';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';

//TODO - move to src_common/utils/time/time.ts util
import dayjs from 'dayjs';

const wordToSearch = (searchQuery: string): string | undefined => {
    return searchQuery.toLowerCase();
};

const ITEMS_PER_PAGE = 50;
const STARTING_PAGE = 0;

export type SortType = 'timeSettings.startTime' | 'name';

const FiltersIO = t.interface({
    range: t.interface({
        startDate: t.string,
        endDate: t.string,
    }),
    sport: t.union([t.string, t.null, t.undefined]),
    country: t.union([t.string, t.null, t.undefined]),
    competition: t.union([t.number, t.null, t.undefined]),
    q: t.union([t.string, t.null, t.undefined]),
});

const isFilters = createGuard(FiltersIO);

type FiltersType = t.TypeOf<typeof FiltersIO>;

const TabIO = t.union([t.literal('details'), t.literal('prices')]);

const isTab = createGuard(TabIO);

type TabType = t.TypeOf<typeof TabIO>;

const filterParamCountry = (
    countryFilter: string | null | undefined,
    list: Array<EventListGroupEventItemType>
): Array<EventListGroupEventItemType> => {
    const country = countryFilter ?? null;

    if (country === null || country === '') {
        return list;
    }

    return list.filter((event) => event.tags['country'] === country);
};

const sortBy = (
    sortDirection: 'up' | 'down',
    sortColumn: SortType,
    list: Array<EventListGroupEventItemType>
): Array<EventListGroupEventItemType> => {
    const listCopy = [...list];

    if (sortColumn === 'name') {
        listCopy.sort((a, b) => {
            if (sortDirection === 'up') {
                if (a.name > b.name) {
                    return 1;
                }
                if (a.name < b.name) {
                    return -1;
                }
            } else {
                if (a.name > b.name) {
                    return -1;
                }
                if (a.name < b.name) {
                    return 1;
                }
            }

            return 0;
        });

        return listCopy;
    }

    listCopy.sort((a, b) => {
        const value1 = a.startTime;
        const value2 = b.startTime;

        if (sortDirection === 'up') {
            return value1 - value2;
        } else {
            return value2 - value1;
        }
    });

    return listCopy;
};

export interface SmallModelRowType {
    id: number;
    name: string;
    startTime: number;
}

export class TradingEventsListState {
    @observable.ref public filters: FiltersType;
    @observable public createEvent: boolean;
    @observable public tab: TabType = 'details';

    @observable public sortDirection: 'up' | 'down' = 'up';
    @observable public sortColumn: SortType = 'timeSettings.startTime';
    @observable public page: number = STARTING_PAGE;

    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly routerBackoffice: RouterBackoffice,
        private readonly sportsbookState: SportsbookState
    ) {
        this.filters = {
            range: {
                startDate: moment().subtract(2, 'hours').seconds(0).toISOString(),
                endDate: moment().endOf('day').add(1, 'days').toISOString(),
            },
            sport: undefined,
            country: undefined,
            competition: undefined,
            q: undefined,
        };

        this.createEvent = false;
        makeAutoObservable(this);
    }

    private getListPage(page: number): EventsCollectionQueryModel | null {
        const sport = this.filters.sport ?? undefined;

        const startDate = dayjs(this.filters.range.startDate).startOf('minute').toISOString();
        const endDate = dayjs(this.filters.range.endDate).startOf('minute').toISOString();

        const query: EventsCollectionQueryType = {
            'startTime[from]': startDate,
            'startTime[to]': endDate,
            display: false,
            sport: sport === undefined ? undefined : [sport],
            competition: this.filters.competition ?? undefined,
            q: wordToSearch(this.filters.q ?? ''),
            page,
            pageSize: ITEMS_PER_PAGE,
        };

        return this.sdkStaff.models.getEventQuery(query);
    }

    @computed private get list(): Array<EventListGroupEventItemType> {
        const result = [];

        for (let i = STARTING_PAGE; i <= this.page; i++) {
            const list = this.getListPage(i);

            if (list !== null) {
                result.push(...list.events);
            }
        }

        return result;
    }

    private get lastPage(): EventsCollectionQueryModel | null {
        return this.getListPage(this.page);
    }

    @computed public get isLoading(): boolean {
        return this.lastPage === null;
    }

    public get isShowMore(): boolean {
        const lastPage = this.lastPage;

        if (lastPage === null) {
            return false;
        }

        return lastPage.events.length === ITEMS_PER_PAGE;
    }

    @computed public get events(): Array<SmallModelRowType> {
        const list = this.list;

        // todo: remove all filters and sorting - everything should be done on server side
        const list1 = filterParamCountry(this.filters.country, list);
        const list2 = sortBy(this.sortDirection, this.sortColumn, list1);
        return list2.map(
            (item): SmallModelRowType => ({
                id: item.id,
                name: item.name,
                startTime: item.startTime,
            })
        );
    }

    @action private setFilters = (filters: FiltersType): void => {
        this.filters = {
            ...filters,
            q: filters.q === '' ? undefined : filters.q,
        };
        this.page = STARTING_PAGE;
    };

    public dispatchFilter = (_field: unknown, _value: unknown, data: unknown): void => {
        if (isFilters(data)) {
            const { range, sport, country, competition, q, ...rest } = data;

            this.setFilters({
                range,
                sport,
                country,
                competition,
                q,
            });

            if (JSON.stringify(rest).length !== 2) {
                console.error('Nadmiarowe dane', rest);
            }

            return;
        }

        console.error('Error decode filters', data);
    };

    public onAddEvent = (): void => {
        this.createEvent = true;
    };

    public hideCreateEvent = (): void => {
        this.createEvent = false;
    };

    public onAddEventFinish = (id: number): void => {
        this.createEvent = false;
        this.routerBackoffice.redirectToTradingEvent(id);
        this.sportsbookState.eventPageState.addEvent(id);
        this.sportsbookState.eventPageState.addToRecentlyViewed(id);
    };

    public changeTab = (tab: string | null | number): void => {
        if (isTab(tab)) {
            this.tab = tab;
        } else {
            console.error('change tab, decode error', tab);
        }
    };

    public sortEvents = (sort: SortType | undefined): void => {
        if (sort === undefined) {
            return;
        }

        if (this.sortColumn === sort) {
            if (this.sortDirection === 'up') {
                this.sortDirection = 'down';
            } else {
                this.sortDirection = 'up';
            }
            return;
        }

        this.sortColumn = sort;
        this.sortDirection = 'down';
    };

    public showMore = (): void => {
        this.page += 1;
    };
}
